import React from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box,
    CircularProgress,
    Typography,
} from "@mui/material";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { useTablePagination } from "hooks";
import {
    useLazyListCodTransferAmountsByVendorQuery,
} from "store/services/PaymentsApi/codTransferApi";

const VendorCODTransferAmoutList = ({ vendorId }) => {
    const [searchParams] = useSearchParams();
    const keyword = searchParams.get("search") || "";
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const [
        fetchTransferAmounts,
        { data: transferRes, isLoading, isFetching },
    ] = useLazyListCodTransferAmountsByVendorQuery();

    React.useEffect(() => {
        if (!vendorId) return;
        fetchTransferAmounts({
            vendorId,
            page: currentPage,
            limit: perPage,
            keyword: keyword || undefined,
        });
    }, [vendorId, currentPage, perPage, fetchTransferAmounts, keyword]);

    const totalTransferAmount = React.useMemo(() => {
        if (!transferRes?.data) return 0;
        return transferRes.data.reduce(
            (sum, row) => sum + (row.totalTransferAmount || 0),
            0
        );
    }, [transferRes]);

    const columns = [
        {
            name: "COD Transfer ID",
            selector: (row) => row?.codTransferId,
            cell: (row) => row?.codTransferId || "N/A",
        },
        {
            name: "Shipment ID",
            selector: (row) => row?.shipmentIds,
            cell: (row) =>
                row?.shipmentIds?.length
                    ? row.shipmentIds.join(", ")
                    : "N/A",
        },
        {
            name: "Transfer Amount",
            selector: (row) => row?.totalTransferAmount,
            cell: (row) => `Rs ${row?.totalTransferAmount?.toFixed(2) || 0}`,
        },
        {
            name: "Transfer Date",
            selector: (row) => row?.transferDate,
            cell: (row) => {
                if (!row?.transferDate) return "N/A";
                return new Date(row.transferDate).toLocaleDateString();
            },
        },
    ];

    return (
        <BreadcrumbLayout.Paper>
            <TableFilter
                searchLabel="Shipment ID"
                searchPlaceholder="Search by Shipment ID..."
                actions={
                    <Typography variant="subtitle1" fontWeight="bold">
                        Total Transfer Amount: Rs {totalTransferAmount.toFixed(2)}
                    </Typography>
                }
            />

            {transferRes ? (
                <AppTable
                    columns={columns}
                    data={transferRes?.data || []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={transferRes?.totalItems || 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            ) : (
                <Box display="flex" alignItems="center" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                    <Typography ml={1}>Loading Transfer Amounts...</Typography>
                </Box>
            )}
        </BreadcrumbLayout.Paper>
    );
};

export default VendorCODTransferAmoutList;